import React, { useState, useRef } from 'react';
import './AudioRecorder.css';

const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';

/**
 * AudioRecorder Component
 * 
 * Responsabilidades (SRP - Single Responsibility Principle):
 * 1. Gravar áudio usando MediaRecorder API
 * 2. Controlar estado de gravação (idle, recording, uploading)
 * 3. Fazer upload via /api/media/upload
 * 4. Notificar pai quando upload completo
 * 
 * NÃO responsável por:
 * - Enviar mensagem WhatsApp (responsabilidade do pai)
 * - Gerenciar conversa (responsabilidade do CRMPage)
 * - Storage de arquivo (responsabilidade do backend)
 */
function AudioRecorder({ onAudioReady, disabled = false }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      setError(null);
      
      // Pedir permissão de microfone
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      // Criar MediaRecorder com codec OGG (WhatsApp friendly)
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      // Coletar chunks de áudio
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      // Quando gravação parar, fazer upload
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: 'audio/ogg; codecs=opus' 
        });
        
        // Upload do áudio
        await uploadAudio(audioBlob);
        
        // Parar stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Iniciar gravação
      mediaRecorder.start();
      setIsRecording(true);
      
      // Timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setError('Erro ao acessar microfone. Verifique as permissões.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Parar timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      // Parar sem processar
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setRecordingTime(0);
      audioChunksRef.current = [];
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const uploadAudio = async (audioBlob) => {
    try {
      setIsUploading(true);
      
      // Criar FormData
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.ogg');
      
      // Upload
      const response = await fetch(`${API_URL}/media/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Upload falhou: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Notificar pai com URL do áudio
      if (onAudioReady) {
        onAudioReady({
          url: data.url,
          filename: data.filename,
          mimetype: data.mimetype,
          size: data.size
        });
      }
      
      setRecordingTime(0);
      
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      setError('Erro ao enviar áudio. Tente novamente.');
    } finally {
      setIsUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Limpar ao desmontar
  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream?.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="audio-recorder">
      {error && (
        <div className="audio-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      {!isRecording && !isUploading && (
        <button
          className="record-btn"
          onClick={startRecording}
          disabled={disabled}
          title="Gravar áudio"
        >
          🎤
        </button>
      )}
      
      {isRecording && (
        <div className="recording-controls">
          <div className="recording-indicator">
            <span className="pulse"></span>
            <span className="time">{formatTime(recordingTime)}</span>
          </div>
          <button 
            className="stop-btn" 
            onClick={stopRecording}
            title="Enviar áudio"
          >
            ✓
          </button>
          <button 
            className="cancel-btn" 
            onClick={cancelRecording}
            title="Cancelar"
          >
            ×
          </button>
        </div>
      )}
      
      {isUploading && (
        <div className="uploading">
          <span className="spinner"></span>
          <span>Enviando áudio...</span>
        </div>
      )}
    </div>
  );
}

export default AudioRecorder;
